import {FC, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";
import {
    HatGraduation20Regular,
    Person20Regular,
    SignOut20Regular,
    Sparkle20Regular
} from "@fluentui/react-icons";
import ClientTreeHierarchyContainer from "@/features/application/components/ClientTreeHierarchyContainer";
import authApi from "@/features/authentication/api/authAPI";
import {AppState} from "@/state/rootReducer";
import {BentoDemoUrlModel, RouteType} from "@/util/appParamsUtil";
import {getCurrentSelectedBusinessUnitId} from "@/util/stateUtil";
import {useBentoIntelligence} from "../BentoIntelligenceContext";
import "./bento-nav-header.scss";

interface BentoNavHeaderProps {
    /** When true, hides the hierarchy picker on the left. */
    hidePicker?: boolean;
}

const BentoNavHeader: FC<BentoNavHeaderProps> = ({hidePicker = false}) => {
    const {openDrawer} = useBentoIntelligence();
    const history = useHistory();
    const location = useLocation();
    const isAcademyActive = location.pathname.startsWith(
        "/par-intelligence/academy"
    );
    const clientConcept = useSelector(
        (state: AppState) => state.application.selectedClientConcept
    );
    const businessUnitId = useSelector((state: AppState) =>
        getCurrentSelectedBusinessUnitId(state)
    );
    const date = useSelector((state: AppState) => state.application.date);
    const userInfo = useSelector((state: AppState) => state.application.userInfo);

    const [menuOpen, setMenuOpen] = useState(false);
    const accountRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (
                accountRef.current &&
                !accountRef.current.contains(event.target as Node)
            ) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [menuOpen]);

    const handleSignOut = async () => {
        try {
            await authApi.logout();
        } finally {
            window.location.href = "/login";
        }
    };

    const urlModel =
        businessUnitId && date
            ? new BentoDemoUrlModel(clientConcept, businessUnitId, date)
            : null;

    return (
        <header className="bento-nav-header">
            <div className="bento-nav-header__left">
                {!hidePicker && urlModel && (
                    <ClientTreeHierarchyContainer
                        urlModel={urlModel}
                        routeType={RouteType.BENTO}
                        variant="bento"
                    />
                )}
            </div>

            <div className="bento-nav-header__right">
                <button
                    type="button"
                    onClick={() => openDrawer()}
                    className="bento-nav-header__item"
                >
                    <Sparkle20Regular className="bento-nav-header__item-icon" />
                    <span>PAR Intelligence</span>
                </button>

                <button
                    type="button"
                    className={`bento-nav-header__item${
                        isAcademyActive ? " bento-nav-header__item--active" : ""
                    }`}
                    aria-current={isAcademyActive ? "page" : undefined}
                    onClick={() => history.push("/par-intelligence/academy")}
                >
                    <HatGraduation20Regular className="bento-nav-header__item-icon" />
                    <span>PAR Academy</span>
                </button>

                <div className="bento-nav-header__account" ref={accountRef}>
                    <button
                        type="button"
                        className="bento-nav-header__icon-btn"
                        aria-label="User account"
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen((open) => !open)}
                    >
                        <Person20Regular />
                    </button>

                    {menuOpen && (
                        <div className="bento-nav-header__menu" role="menu">
                            <div className="bento-nav-header__menu-header">
                                <span className="bento-nav-header__menu-name">
                                    {userInfo.userName}
                                </span>
                                {userInfo.emailAddress && (
                                    <span className="bento-nav-header__menu-email">
                                        {userInfo.emailAddress}
                                    </span>
                                )}
                                {userInfo.clientCode && (
                                    <span className="bento-nav-header__menu-client">
                                        {userInfo.clientCode}
                                    </span>
                                )}
                            </div>

                            <div className="bento-nav-header__menu-divider" />

                            <button
                                type="button"
                                role="menuitem"
                                className="bento-nav-header__menu-item"
                                onClick={handleSignOut}
                            >
                                <SignOut20Regular />
                                <span>Sign out</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default BentoNavHeader;
